import Big from 'big.js';
import { useCallback, useContext, useMemo } from 'react';
import { Link, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerOperationArchiveButton } from '@/modules/issuer-dashboard/components/IssuerOperationArchiveButton.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import { IssuerTransactionsOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { useTransactionArchiveByIdMutation } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { DisclosureListItem, ModalProviderContext } from 'ui';
import { fixNameEnum, goodFormatUSDCurrency } from 'ui/utils';
import { TransactionPaginateResponse, TransactionStatus, TransactionType } from 'api';

export interface IssuerTransactionItemProps extends TransactionPaginateResponse {
  register_id: number;
  isDeleted?: boolean;
  showArchiveButton?: boolean;
}

export const IssuerTransactionItem = ({
  id,
  type,
  memo,
  status,
  quantity,
  issue_date,
  payment_id,
  register_class,
  price_per_share,
  requested_at_date,
  shareholder_entity,
  dividend_event_id,
  register_class_id,
  total,
  isDeleted = false,
  showArchiveButton = false,
}: IssuerTransactionItemProps) => {
  const { pathname } = useLocation();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [params] = useSearchParams();

  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { mutateAsync: archiveTransaction } = useTransactionArchiveByIdMutation();

  const handleOpenFullDividend = useCallback(
    (id: number) => () => {
      openModal(DividendOperationInfoModal, {
        id,
        registerId,
        registerCountry,
        isDeleted,
      });
    },
    [isDeleted, openModal, registerCountry, registerId],
  );

  const handleOpenFullPayment = useCallback(() => {
    if (!payment_id) return;
    openModal(IssuerActivitiesOperationInfoModal, {
      paymentId: payment_id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [isDeleted, openModal, payment_id, registerCountry, registerId]);

  const handleOpenFullTransaction = useCallback(() => {
    if (!id) return;
    openModal(IssuerTransactionsOperationInfoModal, {
      id: id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [isDeleted, openModal, registerCountry, registerId, id]);

  const bottomContentData = useMemo<Array<[JSX.Element | string, JSX.Element | string]>>(() => {
    const info: Array<[JSX.Element | string, JSX.Element | string]> = [];

    if (requested_at_date) {
      info.push(['Request date', dateToPrint(requested_at_date)]);
    }
    if (issue_date) {
      info.push(['Completion date', dateToPrint(issue_date)]);
    }

    if (register_class?.class_name && register_class_id) {
      info.push([
        'Class',
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.classById(register_class_id.toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {register_class?.class_name}
        </Link>,
      ]);
    }

    if (shareholder_entity) {
      info.push([
        'Investor',
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.holder(shareholder_entity.id.toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {shareholder_entity.entity_name}
        </Link>,
      ]);
    }
    if (id) {
      info.push([
        'Transaction ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullTransaction}
        >
          #{id}
        </span>,
      ]);
    }
    if (payment_id) {
      info.push([
        'Payment ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullPayment}
        >
          #{payment_id}
        </span>,
      ]);
    }
    if (dividend_event_id) {
      info.push([
        `${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`,
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullDividend(dividend_event_id)}
        >
          #{dividend_event_id}
        </span>,
      ]);
    }
    if (issue_date) {
      info.push(['Memo', memo || '-']);
    }

    if (!isDeleted) {
      //ToDo: add confirm decide pending transaction
      if (showArchiveButton) {
        info.push([
          '',
          <IssuerOperationArchiveButton
            id={id}
            entityName="transaction"
            onArchive={archiveTransaction}
          />,
        ]);
      }
    }

    return info;
  }, [
    requested_at_date,
    issue_date,
    register_class?.class_name,
    register_class_id,
    shareholder_entity,
    id,
    payment_id,
    dividend_event_id,
    isDeleted,
    pathname,
    params,
    handleOpenFullTransaction,
    handleOpenFullPayment,
    getRegisterTypeStrings,
    handleOpenFullDividend,
    memo,
    showArchiveButton,
    archiveTransaction,
  ]);

  const name = useMemo(() => {
    if (type === TransactionType.Subscription) return getRegisterTypeStrings('application');
    if (type === TransactionType.Drp) return 'Dividend reinvestment';
    return fixNameEnum(type);
  }, [getRegisterTypeStrings, type]);

  const calculateRightMainString = (): string => {
    if (type === TransactionType.Drp) {
      if (
        status === TransactionStatus.Pending &&
        total &&
        price_per_share &&
        register_class?.current_price_per_share
      ) {
        return goodFormatUSDCurrency(
          (total / price_per_share) * (register_class.current_price_per_share ?? 0),
        );
      }

      if (
        status === TransactionStatus.DividendPending ||
        status === TransactionStatus.Unsuccessfull
      ) {
        return goodFormatUSDCurrency(total ?? 0);
      }
    }

    const calculatedValue = new Big(quantity || 0).mul(price_per_share || 0).valueOf();
    return goodFormatUSDCurrency(+calculatedValue);
  };
  return (
    <DisclosureListItem
      leftMainString={name}
      leftSubduedContentSet={[
        issue_date ? dateToPrint(issue_date) : 'No completion date',
        register_class?.class_name || '-',
      ]}
      rightMainString={calculateRightMainString()}
      rightContent={
        <span className="flex gap-4">
          {dividend_event_id && (
            <span
              className={
                'text-[12px] leading-[20px] text-white bg-blue-400 w-fit select-none ' +
                'rounded-[1000px] px-2 py-[2px] capitalize col-start-1 col-end-2'
              }
            >
              {getRegisterTypeStrings('dividend', { capitalize: true })}
            </span>
          )}
          <ActivityStatusBadge status={status || ''} />
        </span>
      }
      classes={{
        leftContent: 'w-full max-w-[500px]',
        rightMainString: 'text-nowrap text-right',
      }}
      rightSubduedString={
        type === TransactionType.Drp &&
        (status === TransactionStatus.Pending ||
          status === TransactionStatus.DividendPending ||
          TransactionStatus.Unsuccessfull) &&
        total
          ? undefined
          : `${formatNumberWithCommas(quantity)} units/shares`
      }
      bottomContent={bottomContentData}
    />
  );
};
